import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Megaphone, Plus } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, ErrorBanner, PageHeader, Spinner, StatusBadge, fmt, useLoad } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";

const L = {
  en: { title: "Campaigns", desc: "Newsletters and announcements to your audiences, sent from your verified domains with one-click unsubscribe.", new: "New campaign",
    none: "No campaigns yet", noneBody: "Create a campaign, let AI write a first draft, send yourself a test and schedule it.", name: "Name", status: "Status", rec: "Recipients", sent: "Sent", when: "Scheduled / finished" },
  fi: { title: "Kampanjat", desc: "Uutiskirjeet ja tiedotteet yleisöillesi, vahvistetuista verkkotunnuksistasi ja yhden klikkauksen tilauksen perumisella.", new: "Uusi kampanja",
    none: "Ei vielä kampanjoita", noneBody: "Luo kampanja, anna tekoälyn kirjoittaa luonnos, lähetä testi itsellesi ja ajasta se.", name: "Nimi", status: "Tila", rec: "Vastaanottajat", sent: "Lähetetty", when: "Ajastettu / valmis" },
};

export default function Campaigns() {
  const { lang } = useT();
  const l = L[lang] || L.en;
  const { can } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const { data, error, loading } = useLoad(() => api.get("/app/campaigns").then((r) => r.data), []);
  const create = async () => {
    setBusy(true);
    try { const { data: c } = await api.post("/app/campaigns", { name: lang === "fi" ? "Nimetön kampanja" : "Untitled campaign" }); nav(`/app/campaigns/${c.id}`); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const action = can("developer") && <Button onClick={create} disabled={busy} data-testid="campaign-create-button"><Plus className="mr-1 h-4 w-4" />{l.new}</Button>;
  return (
    <div>
      <PageHeader eyebrow="Marketing" title={l.title} description={l.desc} actions={action} />
      <ErrorBanner error={error} />
      {loading && !data ? <Spinner /> : data && (data.length === 0 ? <EmptyState icon={Megaphone} title={l.none} body={l.noneBody} action={action} testId="campaigns-empty" /> : (
        <div className="surface overflow-x-auto rounded-2xl"><table className="w-full min-w-[640px] text-left text-sm" data-testid="campaigns-table">
          <thead className="text-xs uppercase tracking-wider text-zinc-500"><tr>{[l.name, l.status, l.rec, l.sent, l.when].map((h) => <th key={h} className="p-4">{h}</th>)}</tr></thead>
          <tbody>{data.map((c) => (
            <tr key={c.id} className="border-t border-white/5 transition-colors hover:bg-white/[0.02]">
              <td className="p-4"><Link to={`/app/campaigns/${c.id}`} className="font-medium text-white hover:text-purple-200" data-testid={`campaign-link-${c.id}`}>{c.name}</Link>
                <div className="text-xs text-zinc-500">{c.subject || "—"}</div></td>
              <td className="p-4"><StatusBadge status={c.status} /></td>
              <td className="p-4 text-zinc-300">{c.audience_size?.toLocaleString() ?? "—"}</td>
              <td className="p-4 text-zinc-300">{c.progress?.sent?.toLocaleString() ?? 0}</td>
              <td className="p-4 text-xs text-zinc-500">{fmt(c.finished_at || c.scheduled_at)}</td>
            </tr>))}</tbody>
        </table></div>))}
    </div>
  );
}
